'use client';

import { useSelector } from 'react-redux';
import MiniCartLineItems from './MiniCartLineItems';
import SkeletonUI from '@next-app/components/Skeleton/Skeleton';
import Conditional from '@next-app/components/Conditional';
import {
  selectIsGuestUser,
  selectScene7ImageUrl,
} from '@next-app/lib/features/InitSelectors';
import {
  selectCommerceItems,
  selectIsCheckoutDetailsLoaded,
  selectIsPureBopisOrder,
  selectIsPureDigitalOrder,
  selectOrderSummaryInfo,
} from '@next-app/lib/features/checkout/CheckoutSelector';
import { useEffect, useState } from 'react';
import { CommerceItemInfo } from '@next-app/lib/features/checkout/Checkout.interface';
import { MiniCartItem } from '@next-app/constants/CheckoutConstants';

const MiniCart = () => {
  // selectors
  const isCheckoutDetailsLoaded = useSelector(selectIsCheckoutDetailsLoaded);
  const isPureDigitalOrder = useSelector(selectIsPureDigitalOrder);
  const isPureBopisOrder = useSelector(selectIsPureBopisOrder);

  const commerceItems = useSelector(selectCommerceItems);
  const {
    webSubTotalInfo,
    instorePickupSubTotalInfo,
    emailDeliverySubtotalInfo,
  } = useSelector(selectOrderSummaryInfo) || {};
  const scene7Url = useSelector(selectScene7ImageUrl);

  const isShipOrderOnly =
    webSubTotalInfo && !instorePickupSubTotalInfo && !emailDeliverySubtotalInfo;

  /**
   * Consonants
   */
  const imageDimensions =
    'wid=64&fmt=pjpeg&pscan=auto&op_sharpen=0&resMode=sharp2&op_usm=1.25,0.5,0,0';

  /**
   * UseState
   */
  const [isFurnitureTrue, setIsFurnitureTrue] = useState(false);
  const [storePickup, setStorePickup] = useState<CommerceItemInfo[]>([]);
  const [shipOrderItem, setShipOrderItem] = useState<CommerceItemInfo[]>([]);
  const [emailDelivery, setEmailDelivery] = useState<CommerceItemInfo[]>([]);
  const { digital, ship, store, none } = MiniCartItem;
  /**
   * UseEffect
   */
  useEffect(() => {
    // Reset the state arrays
    setStorePickup([]);
    setShipOrderItem([]);
    setEmailDelivery([]);

    // Check if commerceItems is defined and not empty
    if (commerceItems && commerceItems?.length > 0) {
      setIsFurnitureTrue(hasFurnitureAndLakeshoreItems(commerceItems));

      // Temporary arrays to hold the new items
      const newStorePickup: CommerceItemInfo[] = [];
      const newShipOrderItem: CommerceItemInfo[] = [];
      const newEmailDelivery: CommerceItemInfo[] = [];

      let currentCategory: string | null = null;

      commerceItems.forEach((item) => {
        switch (item.lineGroupHeader) {
          case digital:
            currentCategory = digital;
            break;
          case store:
            currentCategory = store;
            break;
          case ship:
            currentCategory = ship;
            break;
          case none:
            // Do nothing, keep the current category
            break;
          default:
            currentCategory = null;
            break;
        }

        if (currentCategory === digital) {
          newEmailDelivery.push(item);
        } else if (currentCategory === store) {
          newStorePickup.push(item);
        } else if (currentCategory === ship) {
          newShipOrderItem.push(item);
        }
      });

      // Update the state with the new arrays
      setStorePickup(newStorePickup);
      setShipOrderItem(newShipOrderItem);
      setEmailDelivery(newEmailDelivery);
    }
  }, [commerceItems]);

  /**
   * Methods
   */
  /**
   * hasFurnitureAndLakeshoreItems
   * @param items
   * Returns true if cart has items from both furniture and lakeshore tabs
   */
  const hasFurnitureAndLakeshoreItems = (items: CommerceItemInfo[]) => {
    if (!items || items.length === 0) return false;
    const hasItemsFromLakeshore = items.some((item) => !item.furniture);
    const hasItemsFromFurniture = items.some((item) => item.furniture);

    return hasItemsFromLakeshore && hasItemsFromFurniture;
  };

  /**
   * fullImageURL
   * @param item
   * @returns fully formed url of product image
   */
  const fullImageURL = (item: CommerceItemInfo): string => {
    const largeImageSceneId =
      item?.auxiliaryData?.catalogRef?.largeImageSceneId;
    return `${scene7Url}/${largeImageSceneId}?${imageDimensions}`;
  };

  /**
   * RenderMiniCartTitle
   */
  const RenderMiniCartTitle = ({ title }: { title: string }) => {
    return <div className="fw-bold mb-2">{title}</div>;
  };

  /**
   * RenderMiniCartForShipItem
   */
  const RenderMiniCartForShipItem = () => {
    return (
      <div className="mb-3">
        <RenderMiniCartTitle title={webSubTotalInfo?.headerText} />
        {shipOrderItem?.map((item, index) => (
          <MiniCartLineItems
            key={`shipOrderItem-${index}`}
            productImage={fullImageURL(item)}
            quantity={item.quantity}
            priceInfo={item.priceInfo}
            skuDisplayName={item.skuDisplayName}
            isFurnitureItem={item.furniture}
            showLabel={isFurnitureTrue}
          />
        ))}
      </div>
    );
  };

  /**
   * RenderMiniCartForBopis
   */
  const RenderMiniCartForBopis = () => {
    return (
      <div className="mb-3">
        <RenderMiniCartTitle title={instorePickupSubTotalInfo?.headerText} />
        {storePickup?.map((item, index) => (
          <MiniCartLineItems
            key={`storePickup-${index}`}
            productImage={fullImageURL(item)}
            quantity={item.quantity}
            priceInfo={item.priceInfo}
            skuDisplayName={item.skuDisplayName}
            isFurnitureItem={item.furniture}
            showLabel={isFurnitureTrue}
          />
        ))}
      </div>
    );
  };

  /**
   * RenderMiniCartForDigital
   */
  const RenderMiniCartForDigital = () => {
    return (
      <div className="mb-3">
        <RenderMiniCartTitle title={emailDeliverySubtotalInfo?.headerText} />
        {emailDelivery?.map((item, index) => (
          <MiniCartLineItems
            key={`emailDelivery-${index}`}
            productImage={fullImageURL(item)}
            quantity={item.quantity}
            priceInfo={item.priceInfo}
            skuDisplayName={item.skuDisplayName}
            isFurnitureItem={item.furniture}
            showLabel={isFurnitureTrue}
          />
        ))}
      </div>
    );
  };

  /**
   * RenderMiniCartWithoutCategories
   * this is for guest user / non mix orders
   */
  const RenderMiniCartWithoutCategories = () => {
    return (
      <div className="mb-3">
        {commerceItems?.map((item, index) => (
          <MiniCartLineItems
            key={`gestItem-${index}`}
            productImage={fullImageURL(item)}
            quantity={item.quantity}
            priceInfo={item.priceInfo}
            skuDisplayName={item.skuDisplayName}
            isFurnitureItem={item.furniture}
            showLabel={isFurnitureTrue}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      <Conditional if={!isCheckoutDetailsLoaded}>
        <SkeletonUI width={'100%'} height={30} />
      </Conditional>

      <Conditional if={isCheckoutDetailsLoaded}>
        <>
          <Conditional
            if={
              (isPureBopisOrder || isPureDigitalOrder || isShipOrderOnly) &&
              commerceItems !== undefined
            }
          >
            <RenderMiniCartWithoutCategories />
          </Conditional>
          <Conditional
            if={!(isPureBopisOrder || isPureDigitalOrder || isShipOrderOnly)}
          >
            <>
              <Conditional if={shipOrderItem?.length > 0}>
                <RenderMiniCartForShipItem />
              </Conditional>
              <Conditional if={storePickup?.length > 0}>
                <RenderMiniCartForBopis />
              </Conditional>
              <Conditional if={emailDelivery?.length > 0}>
                <RenderMiniCartForDigital />
              </Conditional>
            </>
          </Conditional>
        </>
      </Conditional>
    </>
  );
};

export default MiniCart;
